import React, { useState } from 'react';
import {
  X,
  ZoomIn,
  ZoomOut,
  Download,
  Store,
  Calendar,
  Hash,
  CheckCircle,
  AlertCircle,
  Eye,
  Loader2,
  Link as LinkIcon,
  FileText,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { useReceipt } from '@/hooks/useReceipts';
import type { ReceiptStatus, ReceiptItem } from '@/types/receipt';
import { RECEIPT_STATUS_LABELS } from '@/types/receipt';

interface ReceiptDetailModalProps {
  receiptId: string | null;
  onClose: () => void;
}

function StatusBadge({ status }: { status: ReceiptStatus }) {
  const Icon = {
    processing: Loader2,
    completed: CheckCircle,
    failed: AlertCircle,
    manual_review: Eye,
  }[status];

  const variant = {
    processing: 'warning',
    completed: 'success',
    failed: 'danger',
    manual_review: 'info',
  }[status] as 'warning' | 'success' | 'danger' | 'info';

  return (
    <Badge variant={variant} dot>
      <Icon className={`w-3 h-3 mr-1 ${status === 'processing' ? 'animate-spin' : ''}`} />
      {RECEIPT_STATUS_LABELS[status]}
    </Badge>
  );
}

function formatCurrency(value: string | number | null | undefined, currency = 'BRL') {
  if (value === null || value === undefined) return '-';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '-';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency,
  }).format(numValue);
}

function formatDate(dateString: string | null | undefined) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function formatDateTime(dateString: string) {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ItemsTable({ items }: { items: ReceiptItem[] }) {
  return (
    <div className="border border-gray-200 dark:border-secondary-700 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-secondary-700/50">
          <tr>
            <th className="text-left px-3 py-2 text-gray-500 dark:text-gray-400 font-medium">
              Item
            </th>
            <th className="text-right px-3 py-2 text-gray-500 dark:text-gray-400 font-medium">
              Qtd
            </th>
            <th className="text-right px-3 py-2 text-gray-500 dark:text-gray-400 font-medium">
              Valor
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-secondary-700">
          {items.map((item, index) => (
            <tr key={index}>
              <td className="px-3 py-2 text-gray-900 dark:text-white">
                {item.description}
              </td>
              <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">
                {item.quantity || 1}
              </td>
              <td className="px-3 py-2 text-right text-gray-900 dark:text-white font-medium">
                {formatCurrency(item.total)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const ReceiptDetailModal: React.FC<ReceiptDetailModalProps> = ({
  receiptId,
  onClose,
}) => {
  const [imageZoom, setImageZoom] = useState(1);
  const [showFullImage, setShowFullImage] = useState(false);
  const { data: receipt, isLoading, isError } = useReceipt(receiptId);

  if (!receiptId) return null;

  const handleDownload = () => {
    if (receipt?.imageUrl) {
      window.open(receipt.imageUrl, '_blank');
    }
  };

  return (
    <>
      <Modal
        isOpen={!!receiptId}
        onClose={onClose}
        title="Detalhes do Recibo"
        size="full"
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : isError || !receipt ? (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <p className="text-red-500 dark:text-red-400">
              Erro ao carregar detalhes do recibo
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Image Column */}
            <div className="space-y-4">
              {/* Image Container */}
              <div
                className="relative bg-gray-100 dark:bg-secondary-700 rounded-lg overflow-hidden cursor-zoom-in"
                style={{ maxHeight: '400px' }}
                onClick={() => setShowFullImage(true)}
              >
                <img
                  src={receipt.imageUrl}
                  alt="Recibo"
                  className="w-full h-full object-contain"
                  style={{ transform: `scale(${imageZoom})` }}
                />
              </div>

              {/* Image Controls */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setImageZoom(Math.max(0.5, imageZoom - 0.25))}
                    disabled={imageZoom <= 0.5}
                  >
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-500">{Math.round(imageZoom * 100)}%</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setImageZoom(Math.min(2, imageZoom + 0.25))}
                    disabled={imageZoom >= 2}
                  >
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleDownload}
                  leftIcon={<Download className="w-4 h-4" />}
                >
                  Download
                </Button>
              </div>

              {/* OCR Confidence */}
              {receipt.ocrConfidence && (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Confianca do OCR: {Math.round(parseFloat(receipt.ocrConfidence) * 100)}%
                </div>
              )}
            </div>

            {/* Details Column */}
            <div className="space-y-6">
              {/* Status & Amount */}
              <div className="flex items-start justify-between">
                <StatusBadge status={receipt.status} />
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(receipt.totalAmount, receipt.currency || 'BRL')}
                  </p>
                  {receipt.taxDeductible && (
                    <Badge variant="success" className="mt-1">
                      Dedutivel
                    </Badge>
                  )}
                </div>
              </div>

              {/* Merchant Info */}
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Store className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Comerciante</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {receipt.merchantName || 'Nao identificado'}
                    </p>
                    {receipt.merchantTaxId && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        CNPJ: {receipt.merchantTaxId}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Data do Recibo</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatDate(receipt.receiptDate)}
                    </p>
                  </div>
                </div>

                {receipt.receiptNumber && (
                  <div className="flex items-start gap-3">
                    <Hash className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Numero</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {receipt.receiptNumber}
                      </p>
                    </div>
                  </div>
                )}

                {receipt.taxCategory && (
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Categoria Fiscal</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {receipt.taxCategory}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Items List */}
              {receipt.items && receipt.items.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Itens ({receipt.items.length})
                  </h4>
                  <ItemsTable items={receipt.items} />
                </div>
              )}

              {/* Linked Transactions */}
              {receipt.transactions && receipt.transactions.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <LinkIcon className="w-4 h-4" />
                    Transacoes Vinculadas ({receipt.transactions.length})
                  </h4>
                  <div className="space-y-2">
                    {receipt.transactions.map((tx) => (
                      <div
                        key={tx.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-secondary-700/50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {tx.description || tx.merchantName || 'Transacao'}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDateTime(tx.createdAt)}
                          </p>
                        </div>
                        <span
                          className={`font-semibold ${
                            tx.type === 'income'
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}
                        >
                          {tx.type === 'income' ? '+' : '-'}
                          {formatCurrency(tx.amount, tx.currency)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="text-sm text-gray-500 dark:text-gray-400 pt-4 border-t border-gray-200 dark:border-secondary-700">
                <p>Criado em: {formatDateTime(receipt.createdAt)}</p>
                {receipt.processingTimeMs && (
                  <p>Tempo de processamento: {receipt.processingTimeMs}ms</p>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Full Image Modal */}
      {showFullImage && receipt && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
          onClick={() => setShowFullImage(false)}
        >
          <button
            onClick={() => setShowFullImage(false)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={receipt.imageUrl}
            alt="Recibo"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default ReceiptDetailModal;
